import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminService } from '@/services/admin';
import { Mail, Send, Eye, FlaskConical, CheckCircle, XCircle, Calendar, User, Stethoscope, Syringe, MessageCircle, Phone, ExternalLink } from 'lucide-react';
import Button from '@/components/ui/Button';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type TabType = 'email' | 'whatsapp';

export default function AdminReminders() {
  const [activeTab, setActiveTab] = useState<TabType>('email');
  const [daysAhead, setDaysAhead] = useState(3);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Email Preview query
  const { data: preview, isLoading: previewLoading, refetch: refetchPreview } = useQuery({
    queryKey: ['admin', 'reminders', 'preview', daysAhead],
    queryFn: () => adminService.previewReminders(daysAhead),
    enabled: false,
  });

  // WhatsApp Reminders query
  const { data: whatsappReminders, isLoading: whatsappLoading, refetch: refetchWhatsapp } = useQuery({
    queryKey: ['admin', 'reminders', 'whatsapp', daysAhead],
    queryFn: () => adminService.getWhatsappReminders(daysAhead),
    enabled: false,
  });

  // Send email mutation
  const sendMutation = useMutation({
    mutationFn: () => adminService.sendReminders(daysAhead),
    onSuccess: (data) => {
      toast.success(`Enviados: ${data.total_sent} emails. Fallidos: ${data.total_failed}`);
      setShowResults(true);
    },
    onError: () => {
      toast.error('Error al enviar recordatorios');
    },
  });

  // Setup email test mutation
  const setupTestMutation = useMutation({
    mutationFn: () => adminService.setupReminderTest(testEmail, daysAhead),
    onSuccess: (data) => {
      toast.success(`Datos de prueba creados para ${data.target_date}`);
      refetchPreview();
    },
    onError: () => {
      toast.error('Error al crear datos de prueba');
    },
  });

  // Setup WhatsApp test mutation
  const setupWhatsappTestMutation = useMutation({
    mutationFn: () => adminService.setupWhatsappTest(testPhone, daysAhead),
    onSuccess: (data) => {
      toast.success(`Datos de prueba creados para ${data.appointment_date}`);
      refetchWhatsapp();
    },
    onError: () => {
      toast.error('Error al crear datos de prueba');
    },
  });

  const handlePreview = () => {
    refetchPreview();
    setShowResults(true);
  };

  const handleWhatsappPreview = () => {
    refetchWhatsapp();
    setShowResults(true);
  };

  const handleSend = () => {
    if (!preview || (preview.total_visits === 0 && preview.total_vaccinations === 0)) {
      toast.error('No hay recordatorios para enviar');
      return;
    }
    sendMutation.mutate();
  };

  const handleSetupTest = () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Ingrese un email valido');
      return;
    }
    setupTestMutation.mutate();
  };

  const handleSetupWhatsappTest = () => {
    if (!testPhone || testPhone.length < 8) {
      toast.error('Ingrese un telefono valido (ej: 11 2345-6789)');
      return;
    }
    setupWhatsappTestMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Recordatorios</h1>
        <p className="text-gray-500 mt-1">
          Prueba y gestion del sistema de recordatorios automaticos
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => { setActiveTab('email'); setShowResults(false); }}
            className={clsx(
              'py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2',
              activeTab === 'email'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            <Mail className="h-4 w-4" />
            Email
          </button>
          <button
            onClick={() => { setActiveTab('whatsapp'); setShowResults(false); }}
            className={clsx(
              'py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2',
              activeTab === 'whatsapp'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp (POC)
          </button>
        </nav>
      </div>

      {/* Email Tab Content */}
      {activeTab === 'email' && (
        <>
          {/* Info Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex gap-3">
              <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Como funciona el sistema de recordatorios por Email</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Se ejecuta automaticamente a las 10:00 y 22:00 (hora Argentina)</li>
                  <li>Envia recordatorios 3 dias antes de proximas visitas y vacunaciones</li>
                  <li>Solo envia a duenos que tienen email registrado</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Panel de Control */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="h-5 w-5 text-gray-600" />
                Vista Previa y Envio
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dias de anticipacion
                  </label>
                  <select
                    value={daysAhead}
                    onChange={(e) => setDaysAhead(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={1}>1 dia</option>
                    <option value={2}>2 dias</option>
                    <option value={3}>3 dias (por defecto)</option>
                    <option value={5}>5 dias</option>
                    <option value={7}>7 dias</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Busca recordatorios para dentro de {daysAhead} dia(s)
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handlePreview}
                    disabled={previewLoading}
                    variant="secondary"
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {previewLoading ? 'Cargando...' : 'Ver Preview'}
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={sendMutation.isPending || !preview || (preview.total_visits === 0 && preview.total_vaccinations === 0)}
                    className="flex-1"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sendMutation.isPending ? 'Enviando...' : 'Enviar Ahora'}
                  </Button>
                </div>

                {/* Resultado del envio */}
                {sendMutation.data && (
                  <div className={clsx(
                    'p-4 rounded-lg border',
                    sendMutation.data.total_failed > 0
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-green-50 border-green-200'
                  )}>
                    <div className="flex items-center gap-2 mb-2">
                      {sendMutation.data.total_failed > 0 ? (
                        <XCircle className="h-5 w-5 text-amber-600" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                      <span className="font-medium">Resultado del envio</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Visitas enviadas: <strong>{sendMutation.data.results.visits_sent}</strong></div>
                      <div>Visitas fallidas: <strong>{sendMutation.data.results.visits_failed}</strong></div>
                      <div>Vacunas enviadas: <strong>{sendMutation.data.results.vaccinations_sent}</strong></div>
                      <div>Vacunas fallidas: <strong>{sendMutation.data.results.vaccinations_failed}</strong></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Panel de Prueba */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-purple-600" />
                Crear Datos de Prueba
              </h2>

              <div className="space-y-4">
                <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
                  Crea un paciente de prueba con recordatorios programados para verificar que los emails lleguen correctamente.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email de prueba
                  </label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El email de recordatorio llegara a esta direccion
                  </p>
                </div>

                <Button
                  onClick={handleSetupTest}
                  disabled={setupTestMutation.isPending || !testEmail}
                  variant="secondary"
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <FlaskConical className="h-4 w-4 mr-2" />
                  {setupTestMutation.isPending ? 'Creando...' : 'Crear Datos de Prueba'}
                </Button>

                {setupTestMutation.data && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2 text-green-800">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Datos creados</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>Paciente: <strong>{setupTestMutation.data.patient.name}</strong></p>
                      <p>Fecha objetivo: <strong>{setupTestMutation.data.target_date}</strong></p>
                      <p>Email: <strong>{setupTestMutation.data.test_email}</strong></p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Preview Results */}
          {showResults && preview && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Recordatorios para {preview.target_date}
                </h2>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-blue-600">
                    <Stethoscope className="h-4 w-4" />
                    {preview.total_visits} visitas
                  </span>
                  <span className="flex items-center gap-1 text-green-600">
                    <Syringe className="h-4 w-4" />
                    {preview.total_vaccinations} vacunas
                  </span>
                </div>
              </div>

              {preview.total_visits === 0 && preview.total_vaccinations === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay recordatorios programados para esta fecha</p>
                  <p className="text-sm mt-1">Usa "Crear Datos de Prueba" para generar algunos</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Visitas */}
                  {preview.visits.length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Stethoscope className="h-4 w-4 text-blue-600" />
                        Proximas Visitas
                      </h3>
                      <div className="space-y-2">
                        {preview.visits.map((visit, idx) => (
                          <div key={idx} className="p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="font-medium text-blue-900">{visit.patient_name}</p>
                              <p className="text-sm text-blue-700">
                                <User className="h-3 w-3 inline mr-1" />
                                {visit.owner_name} ({visit.owner_email})
                              </p>
                              <p className="text-xs text-blue-600 mt-1">{visit.reason}</p>
                            </div>
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                              {visit.tenant_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vacunaciones */}
                  {preview.vaccinations.length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Syringe className="h-4 w-4 text-green-600" />
                        Proximas Vacunaciones
                      </h3>
                      <div className="space-y-2">
                        {preview.vaccinations.map((vacc, idx) => (
                          <div key={idx} className="p-3 bg-green-50 rounded-lg flex items-center justify-between">
                            <div>
                              <p className="font-medium text-green-900">{vacc.patient_name}</p>
                              <p className="text-sm text-green-700">
                                <User className="h-3 w-3 inline mr-1" />
                                {vacc.owner_name} ({vacc.owner_email})
                              </p>
                              <p className="text-xs text-green-600 mt-1">{vacc.vaccine_name}</p>
                            </div>
                            <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">
                              {vacc.tenant_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* WhatsApp Tab Content */}
      {activeTab === 'whatsapp' && (
        <>
          {/* Info Card */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex gap-3">
              <MessageCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium mb-1">Como funciona el sistema de WhatsApp (POC - Costo $0)</p>
                <ul className="list-disc list-inside space-y-1 text-green-700">
                  <li>Genera links wa.me con mensajes pre-escritos</li>
                  <li>El staff abre WhatsApp Web con el numero de la veterinaria</li>
                  <li>Click en el link abre el chat con mensaje listo para enviar</li>
                  <li>El mensaje sale desde el WhatsApp de la veterinaria</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Panel de Control WhatsApp */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="h-5 w-5 text-gray-600" />
                Ver Recordatorios WhatsApp
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dias de anticipacion
                  </label>
                  <select
                    value={daysAhead}
                    onChange={(e) => setDaysAhead(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value={1}>1 dia</option>
                    <option value={2}>2 dias</option>
                    <option value={3}>3 dias (por defecto)</option>
                    <option value={5}>5 dias</option>
                    <option value={7}>7 dias</option>
                  </select>
                </div>

                <Button
                  onClick={handleWhatsappPreview}
                  disabled={whatsappLoading}
                  variant="secondary"
                  className="w-full border-green-300 text-green-700 hover:bg-green-50"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {whatsappLoading ? 'Cargando...' : 'Ver Recordatorios'}
                </Button>
              </div>
            </div>

            {/* Panel de Prueba WhatsApp */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-purple-600" />
                Crear Datos de Prueba WhatsApp
              </h2>

              <div className="space-y-4">
                <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
                  Crea un paciente de prueba con tu telefono para verificar que los links de WhatsApp funcionen.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefono de prueba
                  </label>
                  <input
                    type="text"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="11 2345-6789"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formato Argentina: 11 2345-6789 (sin 0 ni 15)
                  </p>
                </div>

                <Button
                  onClick={handleSetupWhatsappTest}
                  disabled={setupWhatsappTestMutation.isPending || !testPhone}
                  variant="secondary"
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <FlaskConical className="h-4 w-4 mr-2" />
                  {setupWhatsappTestMutation.isPending ? 'Creando...' : 'Crear Datos de Prueba'}
                </Button>

                {setupWhatsappTestMutation.data && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2 text-green-800">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Datos creados - Prueba el link!</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-2">
                      <p>Paciente: <strong>{setupWhatsappTestMutation.data.patient.name}</strong></p>
                      <p>Fecha: <strong>{setupWhatsappTestMutation.data.appointment_date}</strong></p>
                      <a
                        href={setupWhatsappTestMutation.data.whatsapp_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors mt-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Abrir WhatsApp
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* WhatsApp Reminders List */}
          {showResults && whatsappReminders && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Recordatorios WhatsApp para {whatsappReminders.target_date}
                </h2>
                <span className="text-sm text-gray-500">
                  {whatsappReminders.total_reminders} recordatorio(s)
                </span>
              </div>

              {whatsappReminders.total_reminders === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay recordatorios con telefono para esta fecha</p>
                  <p className="text-sm mt-1">Usa "Crear Datos de Prueba" para generar algunos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {whatsappReminders.reminders.map((reminder, idx) => (
                    <div
                      key={idx}
                      className={clsx(
                        'p-4 rounded-lg border flex items-center justify-between',
                        reminder.type === 'visit'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-green-50 border-green-200'
                      )}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {reminder.type === 'visit' ? (
                            <Stethoscope className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Syringe className="h-4 w-4 text-green-600" />
                          )}
                          <span className="font-medium">
                            {reminder.patient_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({reminder.patient_species})
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {reminder.owner_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {reminder.owner_phone}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {reminder.type === 'visit' ? reminder.reason : reminder.vaccine_name}
                        </p>
                      </div>
                      <a
                        href={reminder.whatsapp_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Enviar
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  ))}

                  {/* Instructions */}
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-800 mb-2">Instrucciones:</p>
                    <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
                      <li>Abri WhatsApp Web (web.whatsapp.com) con el numero de la veterinaria</li>
                      <li>Hace click en "Enviar" de cada recordatorio</li>
                      <li>Se abre el chat con el mensaje ya escrito</li>
                      <li>Presiona Enviar en WhatsApp</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
